<template>
  <config-base class="opc-ze" :saas-mode="saasMode" show-plan-details>
    <template slot="config">
      <!-- consumption mode -->
      <component
        v-if="saasMode === EventModeMap.PAY_PER_ATTENDEE"
        :ref="EventModeMap.PAY_PER_ATTENDEE"
        :plan-type="planType"
        :is='configConsumptionRowName'
      />
      <!-- subscription mode -->
      <saas-capacity-config-list
        v-if="saasMode === EventModeMap.SUBSCRIPTION"
        :ref="EventModeMap.SUBSCRIPTION"
        :configRowName="configRowName"
        :config-plan-type="planType"
        :title="title"
        :config-capacity-key="capacityKey"
      />
    </template>
  </config-base>
</template>

<script type="text/jsx">
import _, { map } from 'lodash';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import ConfigList from '@/components/capacity/config-list';
import { BILLING_CYCLE, planTypes, ZOOM_EVENTS_SUB_TYPES } from '@/common/constants';
import plansMixin from '@/plans/mx/plan';
import ConfigBase from '@/plans/EVENT_SAAS/config/base';
import planCommonMixin from '@/plans/EVENT_SAAS/mixin';
import { EventModeMap } from '@/plans/EVENT_SAAS/helper/constant';
import ConfigConsumptionRow from '@/plans/EVENT_SAAS/component/config-consumption-row';
import { isFromZeConsumption } from '@/plans/EVENT_SAAS/helper/utils';
import { getOnlinePlanName } from '@/common/product';
import { plansof } from '@/utils/chain';
import { pageConfig } from '@/common/service/page-config';
import { validDownSellLicense } from '@/utils/validate-downsell';
import { enableNewUIForZS } from '@/common/service/op-switch';
import TwoWayControl from '@/plans/EVENT_SAAS/mixins/two-way-control';
import ConfigConsumptionRowV2 from '@/plans/EVENT_SAAS/component/config-consumption-row-v2';
const SaasCapacityConfigList = {
  mixins: [ConfigList, planCommonMixin, enableNewUIForZS ? TwoWayControl : {}],
  props: {
    title: String,
  },
  data () {
    return {
      mx_selectLabel: this.$t('billing.zoom_events.select_label')
    }
  },
  computed: {
    ...mapGetters(['basePlanTotalLicenses', 'getSelectedPlans', 'getTypeGroupSelectedPlans', 'getSelectedBasePlanInfo', 'getCurrentBasePlanInfo', 'hasBoughtDependPlan', 'boughtPlans']),
    currentSelectedBasePlan() {
      return _.get(this.getSelectedBasePlanInfo?.plans, [0]);
    },
    currentBasePlan() {
      return this.getCurrentBasePlanInfo?.plans?.[0] || {};
    },
    boughtCurrentPlans() {
      return plansof(this.boughtPlans).type(this.configPlanType).get();
    },
    // all plans under the current product type
    planList() {
      return this.planMapFlat.filter((p) => {
        const isBoughtCapacity = map(this.boughtCurrentPlans, this.capacityKey).includes(p[this.capacityKey]);
        return p.type === this.configPlanType && p.isSaas && !isBoughtCapacity;
      });
    },
    // all selected plans under the current product type
    selectedPlanList() {
      return this.getTypeGroupSelectedPlans[this.configPlanType]?.filter((p) => p.isSaas);
    },
    addCapacityBtnVisible() {
      return false;
    },
    mx_capacityTips() {
      return enableNewUIForZS ? '' : this.$t('billing.events.saas.capacityTips', [this.title]);
    },
    editTotalLimitDesc() {
      return this.$t(
        this.hasBoughtDependPlan ?
          'billing.addon_license_limit_tips' :
          'billing.addon_license_limit_tips_without_contact',
        [this.title, this.$t(this.currentBasePlan.parentPlanNameKey || this.currentBasePlan.typeNameKey)]);
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlan', 'setUpdateBasePlan']),
    ...mapActions(['switchSelectPlan']),
    mx_getMaxHosts() {
      return this.basePlanTotalLicenses || 999;
    },
    mx_getMonthlyPlan(capacityPlanList = []) {
      const monthlyPlan = _.find(capacityPlanList, (p) => p.billing_cycle === BILLING_CYCLE.MONTHLY);

      const defaultDisabledBillingCycle = {
        disabled: true,
        disabledInnerDesc: this.$t('billing.events.onlyForAttendeesDesc', [100]),
      };

      const annualAddons = plansof(this.boughtPlans).filter(x => x.baseType == this.$context.type).type(planTypes.ADDITIONAL_CO_EDITOR, planTypes.ADDITIONAL_HOST).annual().get();
      const hasAnnualAddon = !!annualAddons?.length;

      const monthlyTooltips = hasAnnualAddon ? this.$t('billing.planform.can_not_select_monthly', [
        getOnlinePlanName(plansof(this.getSelectedPlans).type(this.$context.type).get(0)),
        getOnlinePlanName(annualAddons[0]),
      ]) : '';

      return {
        plan: monthlyPlan, ...(monthlyPlan ? {
          disabled: hasAnnualAddon,
          tooltips: monthlyTooltips,
        } : { plan: { billing_cycle: BILLING_CYCLE.MONTHLY }, ...defaultDisabledBillingCycle }),
      };
    },
    handleLicenseChange(license) {
      const maxLicense = this.mx_getMaxHosts();
      const shouldShowLicenseAlert = (license >= maxLicense && maxLicense !== 999);
      const { errorMessage } = validDownSellLicense({ quantity: license });

      if (shouldShowLicenseAlert) {
        this.mx_licenseAlert = this.editTotalLimitDesc;
      } else if (errorMessage) {
        this.mx_licenseAlert = errorMessage;
      } else {
        this.mx_licenseAlert = '';
      }
    },
  },
};

export default {
  mixins: [plansMixin, planCommonMixin],
  components: {
    ConfigConsumptionRow,
    ConfigConsumptionRowV2,
    ConfigBase: {
      mixins: [ConfigBase, planCommonMixin],
      computed: { ...mapGetters(['planMapFlat']) },
      mounted() {
        const saasPlan = _.filter(this.planMapFlat, (p) => p.type === this.planType && p.isSaas);

        if (saasPlan?.length === 0 || isFromZeConsumption()) {
          this.title = `${this.$t('billing.planform.title.zoom_events')} ${this.$t('billing.events.ppa.title')}`;
        }
      }
    },
    SaasCapacityConfigList,
  },
  data() {
    return {
      isExpanded: true,
      EventModeMap,
    };
  },
  computed: {
    ...mapGetters(['planMapFlat', 'getTypeGroupSelectedPlans']),
    planList() {
      return this.planMapFlat.filter((p) => p.type === this.planType);
    },
    saasPlans() {
      return _.filter(this.planList, (p) => p.isSaas);
    },
    consumptionPlans() {
      return _.filter(this.planList, (p) => p.isPPA);
    },
    isSassPlansAvailable() {
      const not = this.saasPlans?.length === 0 || isFromZeConsumption();
      return !not;
    },
    selectedPlanList() {
      return this.getTypeGroupSelectedPlans[this.planType];
    },
    selectedSaasPlanList() {
      return _.filter(this.selectedPlanList, (p) => p.isSaas);
    },
    selectedPPAPlanList() {
      return _.filter(this.selectedPlanList, (p) => p.isPPA);
    },
    saasMode() {
      return {
        [ZOOM_EVENTS_SUB_TYPES.EVENT_SAAS_CORE] : EventModeMap.SUBSCRIPTION,
        [ZOOM_EVENTS_SUB_TYPES.EVENT_SAAS_CORE_CONSUMPTION] : EventModeMap.PAY_PER_ATTENDEE,
      }[pageConfig.plan]
    },
    configRowName() {
      if (enableNewUIForZS) {
        return 'CapacityConfigRowV2';
      }
      return 'CapacityConfigRow';
    },
    configConsumptionRowName() {
      if (enableNewUIForZS) {
        return 'ConfigConsumptionRowV2';
      }
      return 'ConfigConsumptionRow';
    }
  }
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-ze {
  &__head {
    &__desc {
      color: #666487;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%;
    }

    &__action {
      &.zm-button {
        padding: 0px;
        margin-left: 4px;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%;
      }

      .zm-button__slot::first-letter {
        text-transform: capitalize;
      }
    }
  }

  &__mode {
    margin-bottom: 24px;

    &__inner {
      display: flex;
      align-items: center;
      gap: 24px;
      margin-bottom: 8px;
    }

    @include media(mobile, tablet) {
      .opc-ze__mode__inner.opc-ze__mode__row {
        flex-direction: column;
        gap: 0;
        align-items: flex-start;
      }
    }

    &__title {
      font-size: 18px;
      font-weight: 700;
      line-height: 27px;
      text-align: left;
    }

    &__diff {
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: 120%;
    }

    &__events-group {
      &.zm-radio-group {
        .zm-radio__inner {
          top: 2px;
        }

        .zm-radio__label--inner {
          text-transform: capitalize;
        }
      }
    }
  }

  &__preview-payment {
    margin-top: 24px;
  }

}
</style>
